<template>
    <div class="snakegame-container" style="touch-action: none; user-select: none; -webkit-touch-callout: none; -webkit-user-select: none;">
        <!-- 遊戲區域 -->
        <div class="game-area" ref="gameAreaRef">

            <!-- 1. 遊戲標題（橙色，橫跨整個頂部） -->
            <div class="game-header">
                <div class="game-topic">{{ gameConfig.topic }}</div>
            </div>

        <!-- 推送確認彈窗 -->
        <PushConfirmModal
            v-model="showPushConfirm"
            :activity-name="gameConfig.topic"
            @confirm="executePush"
        />

            <!-- 2. 計時器（左上角） -->
            <div class="timer">{{ formatTime(timeRemaining) }}
                <button class="start-button" @click="toggleGame">
                    <svg-icon :icon-class="gameStarted ? (gamePaused ? 'play' : 'pause') : 'play'" />
                </button>
            </div>

            <!-- 3. 開始前彈窗 -->
            <div v-if="beforeGame" class="before-game-container">
                <!-- 正式活動模式：三欄佈局（雙人）或兩欄佈局（單人） -->
                <div v-if="isFromActId" class="actid-mode-wrapper" :class="{ 'dual-player-layout': !isSinglePlayerMode }">
                    <div class="actid-mode-modal" :class="{ 'dual-player-layout': !isSinglePlayerMode }">
                        <!-- 左側：左側玩家資訊（僅雙人模式顯示） -->
                        <div v-if="!isSinglePlayerMode" class="student-info-left">
                            <h4 class="student-info-title">{{ $t('Snakegame.leftPlayer') }}</h4>

                            <!-- HiTeach 名單下拉選單模式 -->
                            <template v-if="classRosterSource === 'HT' && classRoster.length > 0">
                                <div class="form-group">
                                    <select
                                        @change="handleLeftRosterSelection"
                                        class="student-input student-select"
                                    >
                                        <option value="">{{ $t('gameActManager.selectStudent') }}</option>
                                        <option
                                            v-for="member in classRoster"
                                            :key="`left-${member.memberName}-${member.seatID}`"
                                            :value="`${member.memberName}-${member.seatID}`"
                                        >
                                            {{ member.seatID }} - {{ member.memberName }}
                                        </option>
                                    </select>
                                    <span v-if="studentInfoErrors.name" class="error-text">{{ studentInfoErrors.name }}</span>
                                </div>

                                <!-- 顯示已選擇的資訊（唯讀） -->
                                <div v-if="studentInfo.name" class="selected-info">
                                    <div class="info-item">
                                        <span class="info-label">{{ $t('gameActManager.seatNumber') }}:</span>
                                        <span class="info-value">{{ studentInfo.seatNumber }}</span>
                                    </div>
                                    <div class="info-item">
                                        <span class="info-label">{{ $t('gameActManager.studentName') }}:</span>
                                        <span class="info-value">{{ studentInfo.name }}</span>
                                    </div>
                                </div>
                            </template>

                            <!-- 手動輸入模式（預設） -->
                            <template v-else>
                                <div class="form-group">
                                    <input
                                        v-model="studentInfo.seatNumber"
                                        type="text"
                                        :placeholder="$t('gameActManager.seatNumberPlaceholder')"
                                        maxlength="3"
                                        class="student-input"
                                    />
                                    <span v-if="studentInfoErrors.seatNumber" class="error-text">{{ studentInfoErrors.seatNumber }}</span>
                                </div>
                                <div class="form-group">
                                    <input
                                        v-model="studentInfo.name"
                                        type="text"
                                        :placeholder="$t('gameActManager.studentNamePlaceholder')"
                                        maxlength="20"
                                        class="student-input"
                                    />
                                    <span v-if="studentInfoErrors.name" class="error-text">{{ studentInfoErrors.name }}</span>
                                </div>
                            </template>
                        </div>

                        <!-- 中間：遊戲資訊和播放按鈕 -->
                        <div class="game-info-center">
                            <div class="game-title">{{ gameConfig.topic }}</div>
                            <div class="game-info">
                                <div class="game-time">{{ $t('Snakegame.gameDuration') }}： {{ gameConfig.gameDuration }} {{ $t('Snakegame.second') }}</div>
                                <div class="game-time">{{ $t('Snakegame.difficulty') }}： {{ getDifficultyText() }}</div>
                            </div>
                            <!-- 按鈕區 -->
                            <div class="center-buttons">
                                <button class="action-button play-button play-in-center" @click="showReadyGoCountdown()">
                                    <svg-icon icon-class="play" class="play-icon" />
                                </button>
                            </div>
                        </div>

                        <!-- 右側：右側玩家資訊 -->
                        <div class="student-info-right">
                            <h4 class="student-info-title">{{ isSinglePlayerMode ? $t('gameActManager.enterStudentInfo') : $t('Snakegame.rightPlayer') }}</h4>

                            <!-- 單人模式：右側輸入/選擇，綁定到 studentInfo -->
                            <template v-if="isSinglePlayerMode">
                                <!-- HiTeach 名單下拉選單模式 -->
                                <template v-if="classRosterSource === 'HT' && classRoster.length > 0">
                                    <div class="form-group">
                                        <select
                                            @change="handleRightRosterSelectionForSingle"
                                            class="student-input student-select"
                                        >
                                            <option value="">{{ $t('gameActManager.selectStudent') }}</option>
                                            <option
                                                v-for="member in classRoster"
                                                :key="`single-${member.memberName}-${member.seatID}`"
                                                :value="`${member.memberName}-${member.seatID}`"
                                            >
                                                {{ member.seatID }} - {{ member.memberName }}
                                            </option>
                                        </select>
                                        <span v-if="studentInfoErrors.name" class="error-text">{{ studentInfoErrors.name }}</span>
                                    </div>

                                    <!-- 顯示已選擇的資訊（唯讀） -->
                                    <div v-if="studentInfo.name" class="selected-info">
                                        <div class="info-item">
                                            <span class="info-label">{{ $t('gameActManager.seatNumber') }}:</span>
                                            <span class="info-value">{{ studentInfo.seatNumber }}</span>
                                        </div>
                                        <div class="info-item">
                                            <span class="info-label">{{ $t('gameActManager.studentName') }}:</span>
                                            <span class="info-value">{{ studentInfo.name }}</span>
                                        </div>
                                    </div>
                                </template>

                                <!-- 手動輸入模式（預設） -->
                                <template v-else>
                                    <div class="form-group">
                                        <input
                                            v-model="studentInfo.seatNumber"
                                            type="text"
                                            :placeholder="$t('gameActManager.seatNumberPlaceholder')"
                                            maxlength="3"
                                            class="student-input"
                                        />
                                        <span v-if="studentInfoErrors.seatNumber" class="error-text">{{ studentInfoErrors.seatNumber }}</span>
                                    </div>
                                    <div class="form-group">
                                        <input
                                            v-model="studentInfo.name"
                                            type="text"
                                            :placeholder="$t('gameActManager.studentNamePlaceholder')"
                                            maxlength="20"
                                            class="student-input"
                                        />
                                        <span v-if="studentInfoErrors.name" class="error-text">{{ studentInfoErrors.name }}</span>
                                    </div>
                                </template>
                            </template>

                            <!-- 雙人模式：右側輸入/選擇，綁定到 studentInfo2 -->
                            <template v-else>
                                <!-- HiTeach 名單下拉選單模式 -->
                                <template v-if="classRosterSource === 'HT' && classRoster.length > 0">
                                    <div class="form-group">
                                        <select
                                            @change="handleRightRosterSelection"
                                            class="student-input student-select"
                                        >
                                            <option value="">{{ $t('gameActManager.selectStudent') }}</option>
                                            <option
                                                v-for="member in classRoster"
                                                :key="`right-${member.memberName}-${member.seatID}`"
                                                :value="`${member.memberName}-${member.seatID}`"
                                            >
                                                {{ member.seatID }} - {{ member.memberName }}
                                            </option>
                                        </select>
                                        <span v-if="studentInfoErrors.name2" class="error-text">{{ studentInfoErrors.name2 }}</span>
                                    </div>

                                    <!-- 顯示已選擇的資訊（唯讀） -->
                                    <div v-if="studentInfo.name2" class="selected-info">
                                        <div class="info-item">
                                            <span class="info-label">{{ $t('gameActManager.seatNumber') }}:</span>
                                            <span class="info-value">{{ studentInfo.seatNumber2 }}</span>
                                        </div>
                                        <div class="info-item">
                                            <span class="info-label">{{ $t('gameActManager.studentName') }}:</span>
                                            <span class="info-value">{{ studentInfo.name2 }}</span>
                                        </div>
                                    </div>
                                </template>

                                <!-- 手動輸入模式（預設） -->
                                <template v-else>
                                    <div class="form-group">
                                        <input
                                            v-model="studentInfo.seatNumber2"
                                            type="text"
                                            :placeholder="$t('gameActManager.seatNumberPlaceholder')"
                                            maxlength="3"
                                            class="student-input"
                                        />
                                        <span v-if="studentInfoErrors.seatNumber2" class="error-text">{{ studentInfoErrors.seatNumber2 }}</span>
                                    </div>
                                    <div class="form-group">
                                        <input
                                            v-model="studentInfo.name2"
                                            type="text"
                                            :placeholder="$t('gameActManager.studentNamePlaceholder')"
                                            maxlength="20"
                                            class="student-input"
                                        />
                                        <span v-if="studentInfoErrors.name2" class="error-text">{{ studentInfoErrors.name2 }}</span>
                                    </div>
                                </template>
                            </template>
                        </div>
                    </div>

                    <!-- 切換遊戲模板區塊（僅在單人模式且允許切換時顯示） -->
                    <div v-if="isSinglePlayerMode && allowTemplateChange" class="game-template-switcher">
                        <div class="switcher-title">{{ $t('gameActManager.switchGameTemplate') }}</div>
                        <div class="switcher-content">
                            <select v-model="selectedGameTemplate" class="template-select">
                                <option value="mole">{{ $t('gameActManager.moleGame') }}</option>
                                <option value="star">{{ $t('gameActManager.starGame') }}</option>
                                <option value="snake">{{ $t('gameActManager.snakeGame') }}</option>
                            </select>
                            <button class="goto-button" @click="navigateToSelectedGame" :disabled="selectedGameTemplate === 'snake'">
                                <svg-icon icon-class="arrowright" />
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Demo/預覽模式：原有佈局 -->
                <div v-else class="before-game-modal">
                    <div class="game-title">{{ gameConfig.topic }}</div>
                    <div class="game-info">
                        <div class="game-time">{{ $t('Snakegame.gameDuration') }}： {{ gameConfig.gameDuration }} {{ $t('Snakegame.second') }}</div>
                        <div class="game-time">{{ $t('Snakegame.difficulty') }}： {{ getDifficultyText() }}</div>
                    </div>
                    <!-- 按鈕區 -->
                    <div class="game-buttons">
                        <button class="action-button play-button" @click="showReadyGoCountdown()">
                            <svg-icon icon-class="play" class="play-icon" />
                        </button>
                        <button v-if="!isPreviewMode" class="action-button setting-button" @click="goToSettings()">
                            <svg-icon icon-class="setting-game" class="setting-icon" />
                        </button>
                        <button v-if="isWebView && !isPreviewMode && isLoggedIn" class="action-button push-button"
                                :class="{ 'disabled': isSendDisabled }"
                                @click="handleSendClick()">
                            <svg-icon icon-class="upload" class="sendweb-icon" />
                        </button>
                        <!-- ✨ 新增：查看成績按鈕（只在 WebView 且已推送時顯示） -->
                        <button v-if="isWebView && !isPreviewMode && pushedActivityId"
                                class="action-button view-scores-button"
                                @click="openScoresPanel"
                                :title="$t('HTwbActKitHome.viewScores') || '查看成績'">
                            <svg-icon icon-class="clipboard-list" class="scores-icon" />
                        </button>
                    </div>
                </div>
            </div>

            <!-- Ready, Go! 提示 -->
            <div v-if="showReadyGo" class="ready-go-container">
                <div class="ready-go-text">{{ readyGoText }}</div>
            </div>

            <!-- 預覽模式標籤（左下角） -->
            <div v-if="isPreviewMode" class="preview-badge-bottom-left">{{ $t('gameActManager.previewMode') }}</div>

            <!-- 遊戲主畫面 -->
            <div class="snake-game-container" ref="snakeGameContainerRef">
                <!-- 分數顯示 -->
                <!-- 單人模式：顯示單一分數 -->
                <div v-if="isSinglePlayerMode" class="score-display">
                    <div class="score-value">{{ score }}</div>
                </div>

                <!-- 雙人模式：顯示兩個玩家的分數 -->
                <div v-else class="dual-score-display">
                    <!-- 左側玩家（綠色） -->
                    <div class="player-score left-player-score">
                        <div class="player-label">{{ $t('Snakegame.leftPlayer') }}</div>
                        <div class="score-value green">{{ leftScore }}</div>
                    </div>
                    <!-- 右側玩家（橘色） -->
                    <div class="player-score right-player-score">
                        <div class="player-label">{{ $t('Snakegame.rightPlayer') }}</div>
                        <div class="score-value orange">{{ rightScore }}</div>
                    </div>
                </div>

                <!-- 遊戲內容區域 -->
                <div class="game-content-wrapper">
                    <!-- 單人模式：左側方向控制鍵（箭頭） -->
                    <div v-if="isSinglePlayerMode" class="direction-controls-left">
                        <button class="direction-btn up-btn" @click="changeDirection('up')"
                                @touchstart.prevent="changeDirection('up')"
                                :disabled="!gameStarted || gamePaused">
                            ↑
                        </button>
                        <div class="direction-row">
                            <button class="direction-btn left-btn" @click="changeDirection('left')"
                                    @touchstart.prevent="changeDirection('left')"
                                    :disabled="!gameStarted || gamePaused">
                                ←
                            </button>
                            <button class="direction-btn down-btn" @click="changeDirection('down')"
                                    @touchstart.prevent="changeDirection('down')"
                                    :disabled="!gameStarted || gamePaused">
                                ↓
                            </button>
                            <button class="direction-btn right-btn" @click="changeDirection('right')"
                                    @touchstart.prevent="changeDirection('right')"
                                    :disabled="!gameStarted || gamePaused">
                                →
                            </button>
                        </div>
                    </div>

                    <!-- 雙人模式：左側 WASD 控制鍵（綠色玩家） -->
                    <div v-else class="direction-controls-left player-left">
                        <button class="direction-btn up-btn green" @click="changeDirection('up', 'left')"
                                @touchstart.prevent="changeDirection('up', 'left')"
                                :disabled="!gameStarted || gamePaused">
                            W
                        </button>
                        <div class="direction-row">
                            <button class="direction-btn left-btn green" @click="changeDirection('left', 'left')"
                                    @touchstart.prevent="changeDirection('left', 'left')"
                                    :disabled="!gameStarted || gamePaused">
                                A
                            </button>
                            <button class="direction-btn down-btn green" @click="changeDirection('down', 'left')"
                                    @touchstart.prevent="changeDirection('down', 'left')"
                                    :disabled="!gameStarted || gamePaused">
                                S
                            </button>
                            <button class="direction-btn right-btn green" @click="changeDirection('right', 'left')"
                                    @touchstart.prevent="changeDirection('right', 'left')"
                                    :disabled="!gameStarted || gamePaused">
                                D
                            </button>
                        </div>
                    </div>

                    <!-- 中央：遊戲棋盤 -->
                    <div class="game-board-wrapper">
                        <div class="game-board" :class="{ 'dual-player-board': !isSinglePlayerMode }" ref="gameBoardRef" :style="boardStyle">
                            <!-- 單人模式：只顯示一條蛇 -->
                            <template v-if="isSinglePlayerMode">
                                <div v-for="(segment, index) in snake" :key="`snake-${index}`"
                                     class="snake-segment" :class="{ 'snake-head': index === 0 }"
                                     :style="getSegmentStyle(segment)">
                                    <img v-if="index === 0" :src="getHeadImage()" class="snake-img" :style="getHeadImageStyle()" />
                                    <img v-else :src="require('@/assets/HTwbActAssets/SnakegameObj/snake-body.svg')" class="snake-img" :style="getBodyImageStyle(index)" />
                                </div>
                            </template>

                            <!-- 雙人模式：顯示兩條蛇 -->
                            <template v-else>
                                <!-- 左側玩家（綠色蛇）-->
                                <div v-for="(segment, index) in leftSnake" :key="`left-snake-${index}`"
                                     class="snake-segment left-player" :class="{ 'snake-head': index === 0 }"
                                     :style="getSegmentStylePlayer(segment)">
                                    <img v-if="index === 0" :src="getHeadImagePlayer('left')" class="snake-img" />
                                    <img v-else :src="require('@/assets/HTwbActAssets/SnakegameObj/snake-body.svg')" class="snake-img" />
                                </div>

                                <!-- 右側玩家（橘色蛇）-->
                                <div v-for="(segment, index) in rightSnake" :key="`right-snake-${index}`"
                                     class="snake-segment right-player" :class="{ 'snake-head': index === 0 }"
                                     :style="getSegmentStylePlayer(segment)">
                                    <img v-if="index === 0" :src="getHeadImagePlayer('right')" class="snake-img" />
                                    <img v-else :src="require('@/assets/HTwbActAssets/SnakegameObj/snake-body-orange.svg')" class="snake-img" />
                                </div>
                            </template>

                            <!-- 食物 -->
                            <div v-for="(food, index) in foods" :key="`food-${index}`"
                                 class="food-item" :style="getFoodStyle(food)">
                                <img :src="require('@/assets/HTwbActAssets/SnakegameObj/orange_star.webp')" class="food-img" />
                                <span class="food-text">{{ food.word }}</span>
                            </div>

                            <!-- 飛行分數動畫 -->
                            <transition-group name="flying-score" tag="div">
                                <div v-for="effect in flyingScoreEffects" :key="effect.id"
                                     class="flying-score-effect"
                                     :style="{
                                         left: effect.x + 'px',
                                         top: effect.y + 'px',
                                         color: effect.isCorrect ? 'blue' : 'red',
                                         opacity: effect.opacity
                                     }">
                                    {{ effect.text }}
                                </div>
                            </transition-group>
                        </div>
                    </div>

                    <!-- 雙人模式：右側箭頭控制鍵（橘色玩家） -->
                    <div v-if="!isSinglePlayerMode" class="direction-controls-right player-right">
                        <button class="direction-btn up-btn orange" @click="changeDirection('up', 'right')"
                                @touchstart.prevent="changeDirection('up', 'right')"
                                :disabled="!gameStarted || gamePaused">
                            ↑
                        </button>
                        <div class="direction-row">
                            <button class="direction-btn left-btn orange" @click="changeDirection('left', 'right')"
                                    @touchstart.prevent="changeDirection('left', 'right')"
                                    :disabled="!gameStarted || gamePaused">
                                ←
                            </button>
                            <button class="direction-btn down-btn orange" @click="changeDirection('down', 'right')"
                                    @touchstart.prevent="changeDirection('down', 'right')"
                                    :disabled="!gameStarted || gamePaused">
                                ↓
                            </button>
                            <button class="direction-btn right-btn orange" @click="changeDirection('right', 'right')"
                                    @touchstart.prevent="changeDirection('right', 'right')"
                                    :disabled="!gameStarted || gamePaused">
                                →
                            </button>
                        </div>
                    </div>
                </div>

            <!-- 4. 遊戲暫停彈窗（完全複製打地鼠） -->
            <div v-if="gameStarted && gamePaused && !gameOver" class="pause-game-container">
                <div class="pause-game-modal">
                    <div class="pause-title">{{ $t('Snakegame.gamePaused') || '活動暫停' }}</div>
                    <div class="pause-buttons">
                        <button class="action-button continue-button" @click="resumeGame()">
                            <svg-icon icon-class="play" class="play-icon" />
                        </button>
                        <button class="action-button restart-button" @click="resetGame()">
                            <svg-icon icon-class="refresh" class="refresh-icon" />
                        </button>
                    </div>
                </div>
            </div>

            <!-- 5. 遊戲結束彈窗 -->
            <div v-if="gameOver" class="game-over-modal">
                <div class="snake-game-result-container game-result-container" :class="{ 'single-player-result': isSinglePlayerMode }" v-show="!showAnswerModal">
                    <!-- 左側結果卡片 -->
                    <div class="result-card-wrap">
                        <div class="win-act" v-show="!isSinglePlayerMode && leftScore > rightScore">
                            <img src="@/assets/HTwbActKitCSS/shine.png" alt="">
                        </div>
                        <div class="result-card left-result">
                            <div class="result-header">
                                <!-- 單人模式顯示「遊戲結束」，雙人模式顯示勝負 -->
                                <div v-if="isSinglePlayerMode" class="result-title snake-result-title">
                                    {{ $t('Snakegame.gameOver') }}
                                </div>
                                <div v-else class="result-title" :class="{ 'win-title': leftScore > rightScore, 'tie-title': leftScore === rightScore, 'lose-title': leftScore < rightScore }">
                                    {{ leftScore > rightScore ? $t('PvPgame.win') : (leftScore === rightScore ? $t('PvPgame.tie') : '') }}
                                </div>
                            </div>
                            <div class="result-score">
                                <div class="star-score">
                                    <span class="score-number">{{ isSinglePlayerMode ? score : leftScore }}</span>
                                </div>
                            </div>
                            <div class="result-details">
                                <div class="detail-item">
                                    {{ $t('Snakegame.correctItems') }}: {{ isSinglePlayerMode ? correctCount : leftCorrectCount }} {{ $t('Snakegame.items') }}
                                </div>
                                <div class="detail-item">
                                    {{ $t('Snakegame.errorItems') }}: {{ isSinglePlayerMode ? errorCount : leftErrorCount }} {{ $t('Snakegame.items') }}
                                </div>
                                <div class="detail-item">
                                    {{ $t('Snakegame.maxLength') }}: {{ isSinglePlayerMode ? maxSnakeLength : leftMaxSnakeLength }}
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- 右側結果卡片（單人模式隱藏） -->
                    <div v-if="!isSinglePlayerMode" class="result-card-wrap" v-show="!showAnswerModal">
                        <div class="win-act" v-show="leftScore < rightScore">
                            <img src="@/assets/HTwbActKitCSS/shine.png" alt="">
                        </div>
                        <div class="result-card right-result">
                            <div class="result-header">
                                <div class="result-title" :class="{ 'win-title': rightScore > leftScore, 'tie-title': rightScore === leftScore, 'lose-title': rightScore < leftScore }">
                                    {{ rightScore > leftScore ? $t('PvPgame.win') : (rightScore === leftScore ? $t('PvPgame.tie') : '') }}
                                </div>
                            </div>
                            <div class="result-score">
                                <div class="star-score">
                                    <span class="score-number">{{ rightScore }}</span>
                                </div>
                            </div>
                            <div class="result-details">
                                <div class="detail-item">
                                    {{ $t('Snakegame.correctItems') }}: {{ rightCorrectCount }} {{ $t('Snakegame.items') }}
                                </div>
                                <div class="detail-item">
                                    {{ $t('Snakegame.errorItems') }}: {{ rightErrorCount }} {{ $t('Snakegame.items') }}
                                </div>
                                <div class="detail-item">
                                    {{ $t('Snakegame.maxLength') }}: {{ rightMaxSnakeLength }}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- 答案彈窗 -->
                <div v-if="showAnswerModal" class="answer-modal-mask">
                    <div class="answer-modal-card">
                        <div class="answer-modal-header">
                            <span class="answer-modal-title">{{ $t('Snakegame.referenceAnswers') }}</span>
                            <button class="answer-modal-close" @click="toggleAnswerModal">×</button>
                        </div>
                        <div class="answer-modal-section">
                            <div class="answer-label correct">✔</div>
                            <div class="answer-list">
                                <span v-for="(ans, idx) in gameConfig.vocabularyList" :key="'c-' + idx"
                                      class="answer-item correct">{{ ans }}</span>
                            </div>
                        </div>
                        <div class="answer-modal-section">
                            <div class="answer-label wrong">✘</div>
                            <div class="answer-list">
                                <span v-for="(ans, idx) in gameConfig.errorVocabularyList" :key="'e-' + idx"
                                      class="answer-item wrong">{{ ans }}</span>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- 底部按鈕區 -->
                <div class="game-result-actions" v-show="!showAnswerModal">
                    <button class="action-button answer-button" @click="toggleAnswerModal">
                        <span class="answer-text">A</span>
                    </button>
                    <button class="action-button restart-button" @click="resetGame">
                        <svg-icon icon-class="refresh" class="refresh-icon" />
                    </button>
                </div>
            </div>
        </div>
    </div>
    </div>

    <!-- ✨ 新增：成績查看面板 -->
    <ActivityScoresPanel
      v-if="showScoresPanel"
      :activity-id="pushedActivityId"
      :activity-name="pushedActivityName"
      :roster-source="classRosterSource || 'general'"
      @back="closeScoresPanel"
    />
</template>

<script setup>
import { ref, reactive, onMounted, onUnmounted, computed, inject, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import SvgIcon from '@/components/SvgIcon.vue';
import ActivityScoresPanel from '@/GameActManager/components/ActivityScoresPanel.vue';
import PushConfirmModal from '@/GameActManager/components/PushConfirmModal.vue';
import gameActivityAPI from '@/api/gameActivityAPI';
import api from '@/api';
import jwt_decode from 'jwt-decode';
import { validateAndRedirectGame } from '@/utils/gameActivityRouter';
import { useGameMessageBridge } from '@/composable/useGameMessageBridge';
import { useGameActivityPush } from '@/composable/useGameActivityPush';
import { useHiTeachVersion } from '@/composable/useHiTeachVersion';

const router = useRouter();
const { t } = useI18n();
const $Message = inject('$Message');

// HiTeach 版本管理
const { htVersion, isNewPushFuncVersion, loadVersion } = useHiTeachVersion('HTSnakeGamePlay');

// 使用遊戲訊息橋接器 Composable（處理 Init.GameHome 和使用者驗證）
useGameMessageBridge('HTSnakeGamePlay');

// ==================== 遊戲模式相關 ====================
const isPreviewMode = ref(false);
const isFromActId = ref(false); // 是否從 actId 進入（正式活動模式）
const playerCount = ref(1); // 1: 單人, 2: 雙人
const isSinglePlayerMode = computed(() => playerCount.value === 1);

// 遊戲模板切換相關
const hasGameSwitched = ref(false); // 是否已經切換過遊戲
const allowTemplateChange = ref(true); // 活動是否允許切換遊戲模板
const selectedGameTemplate = ref('snake'); // 當前選擇的遊戲模板（預設為貪食蛇）

// HiTeach 名單相關
const classRosterSource = ref(''); // 名單來源
const classRoster = ref([]); // 解析後的名單陣列
const useHiTeachRoster = ref(false); // 是否使用 HiTeach 名單

// 學生資訊（正式活動模式使用）
const studentInfo = reactive({
  name: '',
  seatNumber: '',
  name2: '',  // 第二位玩家姓名
  seatNumber2: ''  // 第二位玩家座號
});

const studentInfoErrors = reactive({
  name: '',
  seatNumber: '',
  name2: '',
  seatNumber2: ''
});

// 成績提交狀態
const scoreSubmitted = ref(false);

// ==================== 通信工具類 (MessageBridge) ====================
const MessageBridge = {
    // 環境類型
    ENV: {
        WEBVIEW: 'webview',
        WEB: 'web'
    },
    // 當前環境
    currentEnv: 'webview',
    // 初始化
    init() {
        this.currentEnv = window?.chrome?.webview ? this.ENV.WEBVIEW : this.ENV.WEB;
        return this;
    },
    // 發送消息
    postMessage(data, origin = '*') {
        if (this.currentEnv === this.ENV.WEBVIEW) {
            window?.chrome?.webview.postMessage(data);
        } else {
            window.postMessage(data, origin);
        }
    },
    // 添加消息監聽器
    addEventListener(callback) {
        const wrappedCallback = (event) => callback(event);
        // 將包裝後的回調存儲起來，以便後續移除
        if (!this._listeners) {
            this._listeners = new WeakMap();
        }
        this._listeners.set(callback, wrappedCallback);

        if (this.currentEnv === this.ENV.WEBVIEW) {
            window?.chrome?.webview.addEventListener('message', wrappedCallback);
        } else {
            window.addEventListener('message', wrappedCallback);
        }
    },
    // 移除消息監聽器
    removeEventListener(callback) {
        if (!this._listeners) {
            return;
        }
        const wrappedCallback = this._listeners.get(callback);
        if (!wrappedCallback) {
            return;
        }

        if (this.currentEnv === this.ENV.WEBVIEW) {
            window?.chrome?.webview.removeEventListener('message', wrappedCallback);
        } else {
            window.removeEventListener('message', wrappedCallback);
        }
        this._listeners.delete(callback);
    }
};

// 初始化消息橋接器
const messageBridge = MessageBridge.init();

// WebView 環境檢測
const isWebView = computed(() => {
    return messageBridge.currentEnv === MessageBridge.ENV.WEBVIEW;
});

// 推送按鈕冷卻狀態
const isSendDisabled = ref(false);
const cooldownTimer = ref(null);

// ✨ 成績查看面板相關狀態
const showScoresPanel = ref(false);
const pushedActivityId = ref('');  // 儲存最新推送的活動 ID
const pushedActivityName = ref(''); // 儲存活動名稱

// 推送確認彈窗狀態
const showPushConfirm = ref(false);

// 監聽器引用（用於清理）
let messageHandlerRef = null;

// 短網址域名配置
// 短網址域名配置（已移至 useGameActivityPush composable 中）
// const shortURLHost = computed(() => {
//     return locale.value === 'zh-CN' ? 'hiteach.cn' : 'hiteach.cc';
// });

// 使用統一的遊戲活動推送 composable
const { createAndPushShortUrl, isLoggedIn } = useGameActivityPush({
    gameName: 'snake',
    gameType: 'snakegame',
    gameRoute: '/ht-snakegame-play',
    questionType: 'recognition',
    messageBridge: messageBridge,
    htVersion: htVersion,
    serializeGameConfig: (config) => JSON.stringify({
        topic: config.topic,
        vocabularyList: [...config.vocabularyList], // 複製詞彙列表，避免修改原始資料
        errorVocabularyList: [...config.errorVocabularyList],
        difficulty: config.difficulty,
        gameDuration: config.gameDuration,
        boardSize: config.boardSize,
        snakeSpeed: config.snakeSpeed,
        foodCount: config.foodCount
    })
});

// ==================== 遊戲設定 ====================
const gameConfig = reactive({
    topic: 'Choose Tech (Demo)',
    vocabularyList: ['computer', 'keyboard', 'monitor', 'mouse', 'printer', 'scanner', 'laptop', 'tablet', 'router', 'speaker'],
    errorVocabularyList: ['chair', 'table', 'window', 'door', 'lamp', 'book', 'pen', 'paper', 'desk', 'clock'],
    difficulty: 'easy',
    gameDuration: 60,
    boardSize: 15,
    snakeSpeed: 300,
    foodCount: 6,
    wordBatchRefreshSec: 8
});

// ==================== 遊戲狀態 ====================
const beforeGame = ref(true);
const showReadyGo = ref(false);
const readyGoText = ref('');
const gameStarted = ref(false);
const gamePaused = ref(false);
const gameOver = ref(false);
const showAnswerModal = ref(false);

// ==================== 音效（引用打地鼠/摘星星） ====================
const readySound = ref(null);
const goSound = ref(null);
const gameOverSound = ref(null);
const collectSound = ref(null);
const wrongSound = ref(null);
const backgroundMusic = ref(null);
const isMuted = ref(false);

// ==================== 計時器 ====================
const timeRemaining = ref(gameConfig.gameDuration);
let gameTimer = null;
let gameLoopInterval = null;
let wordRefreshInterval = null;

// ==================== 遊戲資料 ====================
// 單人模式資料（保持向後兼容）
const score = ref(0);
const correctCount = ref(0);
const errorCount = ref(0);
const maxSnakeLength = ref(3);

// 蛇的資料結構（單人模式 / 雙人模式的左側玩家）
const snake = ref([
    { x: 10, y: 10 },
    { x: 9, y: 10 },
    { x: 8, y: 10 }
]);
const direction = ref('right');
const nextDirection = ref('right');

// ==================== 雙人模式專用資料 ====================
// 左側玩家（綠色蛇）
const leftSnake = ref([]);
const leftDirection = ref('right');
const leftNextDirection = ref('right');
const leftScore = ref(0);
const leftCorrectCount = ref(0);
const leftErrorCount = ref(0);
const leftMaxSnakeLength = ref(3);

// 右側玩家（橘色蛇）
const rightSnake = ref([]);
const rightDirection = ref('left');
const rightNextDirection = ref('left');
const rightScore = ref(0);
const rightCorrectCount = ref(0);
const rightErrorCount = ref(0);
const rightMaxSnakeLength = ref(3);

// 食物陣列
const foods = ref([]);

// 飛行分數特效
const flyingScoreEffects = ref([]);
let effectIdCounter = 0;

// ==================== DOM 引用 ====================
const gameAreaRef = ref(null);
const gameBoardRef = ref(null);
const snakeGameContainerRef = ref(null);

// ==================== 棋盤像素大小（隨容器縮放） ====================
// 實際渲染的棋盤邊長（px），預設 700，會在 mounted 後由 ResizeObserver 自動更新
const boardPixels = ref(700);

const updateBoardPixels = () => {
    const el = gameBoardRef.value;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    // 棋盤為正方形，取寬度即可
    const size = Math.max(0, Math.floor(rect.width));
    if (size && size !== boardPixels.value) {
        boardPixels.value = size;
    }
};

let boardResizeObserver = null;

// ==================== 計算屬性 ====================
const boardStyle = computed(() => ({
    display: 'grid',
    gridTemplateColumns: `repeat(${gameConfig.boardSize}, 1fr)`,
    gridTemplateRows: `repeat(${gameConfig.boardSize}, 1fr)`,
    // 寬高交由 CSS 響應式與容器控制時，Inline 可能被 !important 覆蓋；
    // 但我們仍保留此值，若無樣式覆蓋時可生效
    width: `${boardPixels.value}px`,
    height: `${boardPixels.value}px`,
    backgroundColor: '#e8f5e9',
    border: '3px solid #4caf50',
    borderRadius: '10px',
    position: 'relative',
    '--board-size': gameConfig.boardSize
}));

// 以實際棋盤像素大小換算每格像素（確保縮放後物件對齊不跑版）
const cellSizeX = computed(() => boardPixels.value / gameConfig.boardSize);
const cellSizeY = computed(() => boardPixels.value / gameConfig.boardSize);
const starSizePx = computed(() => Math.floor(Math.min(cellSizeX.value, cellSizeY.value) * 1.5));

// ==================== 工具函數 ====================
const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
};

const getDifficultyText = () => {
    const map = {
        easy: t('Snakegame.difficultyEasy'),
        medium: t('Snakegame.difficultyMedium'),
        hard: t('Snakegame.difficultyHard')
    };
    return map[gameConfig.difficulty] || '';
};

const getSegmentStyle = (segment) => {
    // 改用絕對定位，與食物星星使用相同的定位方式，確保完全對齊格子
    const left = segment.x * cellSizeX.value;
    const top = segment.y * cellSizeY.value;

    return {
        position: 'absolute',
        left: `${Math.floor(left)}px`,
        top: `${Math.floor(top)}px`,
        width: `${cellSizeX.value}px`,
        height: `${cellSizeY.value}px`
    };
};

const getFoodStyle = (food) => {
    // 完全使用絕對定位，不依賴 grid，避免晃動
    // 移除隨機偏移，確保星星位置與 grid 座標完全對齊
    const left = food.x * cellSizeX.value;
    const top = food.y * cellSizeY.value;

    return {
        position: 'absolute',
        left: `${Math.floor(left)}px`,
        top: `${Math.floor(top)}px`,
        width: `${cellSizeX.value}px`,
        height: `${cellSizeY.value}px`,
        '--starSize': `${starSizePx.value}px`
    };
};

const getHeadImage = () => {
    const directionMap = {
        up: require('@/assets/HTwbActAssets/SnakegameObj/snake-head-up.svg'),
        down: require('@/assets/HTwbActAssets/SnakegameObj/snake-head-down.svg'),
        left: require('@/assets/HTwbActAssets/SnakegameObj/snake-head-left.svg'),
        right: require('@/assets/HTwbActAssets/SnakegameObj/snake-head-right.svg')
    };
    return directionMap[direction.value];
};

// 雙人模式：取得玩家蛇的片段樣式
const getSegmentStylePlayer = (segment) => {
    const left = segment.x * cellSizeX.value;
    const top = segment.y * cellSizeY.value;

    return {
        position: 'absolute',
        left: `${Math.floor(left)}px`,
        top: `${Math.floor(top)}px`,
        width: `${cellSizeX.value}px`,
        height: `${cellSizeY.value}px`
    };
};

// 雙人模式：取得玩家蛇頭圖片
const getHeadImagePlayer = (player) => {
    const dir = player === 'left' ? leftDirection.value : rightDirection.value;
    const colorSuffix = player === 'right' ? '-orange' : '';

    const directionMap = {
        up: require(`@/assets/HTwbActAssets/SnakegameObj/snake-head-up${colorSuffix}.svg`),
        down: require(`@/assets/HTwbActAssets/SnakegameObj/snake-head-down${colorSuffix}.svg`),
        left: require(`@/assets/HTwbActAssets/SnakegameObj/snake-head-left${colorSuffix}.svg`),
        right: require(`@/assets/HTwbActAssets/SnakegameObj/snake-head-right${colorSuffix}.svg`)
    };
    return directionMap[dir];
};

const isSegmentVertical = (index) => {
    if (index === 0) return false;
    const curr = snake.value[index];
    const prev = snake.value[index - 1];
    const next = snake.value[index + 1];
    if (prev && next) return (prev.x === curr.x && next.x === curr.x);
    if (prev) return prev.x === curr.x;
    if (next) return next.x === curr.x;
    return false;
};

const getBodyImageStyle = (index) => {
    const base = Math.min(cellSizeX.value, cellSizeY.value);
    const overlapPxH = Math.round(base * 0.24);
    const overlapPxV = Math.round(base * 0.10);
    if (isSegmentVertical(index)) {
        return {
            transform: 'rotate(90deg)',
            marginTop: `-${Math.floor(overlapPxV / 2)}px`,
            marginBottom: `-${Math.ceil(overlapPxV / 2)}px`
        };
    } else {
        return {
            marginLeft: `-${Math.floor(overlapPxH / 2)}px`,
            marginRight: `-${Math.ceil(overlapPxH / 2)}px`
        };
    }
};

const getHeadImageStyle = () => {
    const base = Math.min(cellSizeX.value, cellSizeY.value);
    const overlapPxH = Math.round(base * 0.24);
    const overlapPxV = Math.round(base * 0.10);
    if (direction.value === 'up' || direction.value === 'down') {
        return {
            marginTop: `-${Math.floor(overlapPxV / 2)}px`,
            marginBottom: `-${Math.ceil(overlapPxV / 2)}px`
        };
    } else {
        return {
            marginLeft: `-${Math.floor(overlapPxH / 2)}px`,
            marginRight: `-${Math.ceil(overlapPxH / 2)}px`
        };
    }
};

// ==================== HiTeach 名單選擇處理 ====================
// 處理左側玩家 HiTeach 名單選擇（雙人模式）
const handleLeftRosterSelection = (event) => {
    const selectedValue = event.target.value;

    if (!selectedValue) {
        // 清空選項
        studentInfo.name = '';
        studentInfo.seatNumber = '';
        // 清除錯誤訊息
        studentInfoErrors.name = '';
        studentInfoErrors.seatNumber = '';
        return;
    }

    // 從名單中找到對應的成員
    const selectedMember = classRoster.value.find(
        member => `${member.memberName}-${member.seatID}` === selectedValue
    );

    if (selectedMember) {
        studentInfo.name = selectedMember.memberName;
        studentInfo.seatNumber = String(selectedMember.seatID);
        // 清除錯誤訊息
        studentInfoErrors.name = '';
        studentInfoErrors.seatNumber = '';
        console.log('👤 左側玩家選擇學生:', selectedMember);
    }
};

// 處理右側玩家 HiTeach 名單選擇（雙人模式）
const handleRightRosterSelection = (event) => {
    const selectedValue = event.target.value;

    if (!selectedValue) {
        // 清空選項
        studentInfo.name2 = '';
        studentInfo.seatNumber2 = '';
        // 清除錯誤訊息
        studentInfoErrors.name2 = '';
        studentInfoErrors.seatNumber2 = '';
        return;
    }

    // 從名單中找到對應的成員
    const selectedMember = classRoster.value.find(
        member => `${member.memberName}-${member.seatID}` === selectedValue
    );

    if (selectedMember) {
        studentInfo.name2 = selectedMember.memberName;
        studentInfo.seatNumber2 = String(selectedMember.seatID);
        // 清除錯誤訊息
        studentInfoErrors.name2 = '';
        studentInfoErrors.seatNumber2 = '';
        console.log('👤 右側玩家選擇學生（雙人）:', selectedMember);
    }
};

// 處理單人模式下的 HiTeach 名單選擇（共用右側欄位）
const handleRightRosterSelectionForSingle = (event) => {
    const selectedValue = event.target.value;

    if (!selectedValue) {
        // 清空選項
        studentInfo.name = '';
        studentInfo.seatNumber = '';
        // 清除錯誤訊息
        studentInfoErrors.name = '';
        studentInfoErrors.seatNumber = '';
        return;
    }

    // 從名單中找到對應的成員
    const selectedMember = classRoster.value.find(
        member => `${member.memberName}-${member.seatID}` === selectedValue
    );

    if (selectedMember) {
        studentInfo.name = selectedMember.memberName;
        studentInfo.seatNumber = String(selectedMember.seatID);
        // 清除錯誤訊息
        studentInfoErrors.name = '';
        studentInfoErrors.seatNumber = '';
        console.log('👤 單人模式選擇學生:', selectedMember);
    }
};

// ==================== 遊戲控制 ====================
const showReadyGoCountdown = () => {
    // 正式活動模式：驗證學生資訊
    if (isFromActId.value) {
        // 清空錯誤訊息
        studentInfoErrors.name = '';
        studentInfoErrors.seatNumber = '';
        studentInfoErrors.name2 = '';
        studentInfoErrors.seatNumber2 = '';

        // 驗證玩家1姓名
        if (!studentInfo.name.trim()) {
            studentInfoErrors.name = t('gameActManager.studentNameRequired');
            return;
        }
        if (studentInfo.name.length > 20) {
            studentInfoErrors.name = t('gameActManager.studentNameTooLong');
            return;
        }

        // 驗證玩家1座號（選填，但如果填寫必須符合格式）
        if (studentInfo.seatNumber && !/^\d{1,3}$/.test(studentInfo.seatNumber)) {
            studentInfoErrors.seatNumber = t('gameActManager.seatNumberInvalid');
            return;
        }

        // 雙人模式：驗證玩家2資訊
        if (!isSinglePlayerMode.value) {
            if (!studentInfo.name2.trim()) {
                studentInfoErrors.name2 = t('gameActManager.studentNameRequired');
                return;
            }
            if (studentInfo.name2.length > 20) {
                studentInfoErrors.name2 = t('gameActManager.studentNameTooLong');
                return;
            }
            if (studentInfo.seatNumber2 && !/^\d{1,3}$/.test(studentInfo.seatNumber2)) {
                studentInfoErrors.seatNumber2 = t('gameActManager.seatNumberInvalid');
                return;
            }

            // 使用 HiTeach 名單時，檢查兩方座號是否相同
            if (classRosterSource.value === 'HT' && classRoster.value.length > 0) {
                if (studentInfo.seatNumber.trim() === studentInfo.seatNumber2.trim()) {
                    studentInfoErrors.name2 = t('gameActManager.seatNumberAlreadyUsed');
                    return;
                }
            }
        }

        // 儲存到 sessionStorage
        sessionStorage.setItem('studentInfo', JSON.stringify({
            name: studentInfo.name.trim(),
            seatNumber: studentInfo.seatNumber.trim(),
            name2: isSinglePlayerMode.value ? '' : studentInfo.name2.trim(),
            seatNumber2: isSinglePlayerMode.value ? '' : studentInfo.seatNumber2.trim()
        }));
    }

    // 嘗試在使用者互動當下解鎖背景音樂播放權限（行動裝置/瀏覽器自動播放政策）
    primeBackgroundMusic();

    // 停止並重置音效時間軸
    if (readySound.value) { readySound.value.pause(); readySound.value.currentTime = 0; }
    if (goSound.value) { goSound.value.pause(); goSound.value.currentTime = 0; }
    if (gameOverSound.value) { gameOverSound.value.pause(); gameOverSound.value.currentTime = 0; }

    beforeGame.value = false;
    showReadyGo.value = true;
    readyGoText.value = 'Ready';
    // 播放 Ready 音效
    playSound(readySound.value);
    // 與打地鼠一致：顯示 Ready -> Go!
    setTimeout(() => {
        readyGoText.value = 'Go!';
        // 播放 Go 音效
        playSound(goSound.value);
        // 提前在 Go 階段啟動背景音樂，避免單人模式部分瀏覽器未自動播放
        if (backgroundMusic.value) {
            // eslint-disable-next-line no-empty
            try { backgroundMusic.value.currentTime = 0; backgroundMusic.value.play(); } catch (e) {}
        }
        setTimeout(() => {
            showReadyGo.value = false;
            startGame();
        }, 500);
    }, 800);
};

// 於使用者點擊開始鍵時，先行播放再暫停，解鎖音訊通道
const primeBackgroundMusic = () => {
    if (!backgroundMusic.value) return;
    try {
        const vol = backgroundMusic.value.volume;
        // 可選：降低音量避免瞬間爆音
        backgroundMusic.value.volume = Math.min(0.01, vol);
        const p = backgroundMusic.value.play();
        if (p && typeof p.then === 'function') {
            p.then(() => {
                backgroundMusic.value.pause();
                backgroundMusic.value.currentTime = 0;
                backgroundMusic.value.volume = vol;
            }).catch(() => {
                // 若被拒絕則忽略，稍後 startGame 時再嘗試
                backgroundMusic.value.volume = vol;
            });
        } else {
            // 同步情況
            backgroundMusic.value.pause();
            backgroundMusic.value.currentTime = 0;
            backgroundMusic.value.volume = vol;
        }
    } catch (e) {
        // ignore
    }
};

const startGame = () => {
    gameStarted.value = true;
    gamePaused.value = false;
    initializeFoods();
    startTimer();
    startGameLoop();
    startWordRefresh();
    // 播放背景音樂
    if (backgroundMusic.value) {
        // eslint-disable-next-line no-empty
        try { backgroundMusic.value.currentTime = 0; backgroundMusic.value.play(); } catch (e) {}
    }
};

const toggleGame = () => {
    if (!gameStarted.value) {
        showReadyGoCountdown();
    } else if (gamePaused.value) {
        resumeGame();
    } else {
        pauseGame();
    }
};

// 前往設定頁面
const goToSettings = () => {
    // 先將當前遊戲配置儲存到 sessionStorage，讓設定頁面可以讀取
    sessionStorage.setItem('SnakeGameConfig', JSON.stringify({
        topic: gameConfig.topic,
        correctVocabularyList: [...gameConfig.vocabularyList],
        errorVocabularyList: [...gameConfig.errorVocabularyList],
        difficulty: gameConfig.difficulty,
        gameDuration: gameConfig.gameDuration,
        boardSize: gameConfig.boardSize,
        snakeSpeed: gameConfig.snakeSpeed,
        foodCount: gameConfig.foodCount
    }));
    router.push('/ht-snakegame-setting');
};

// 從當前遊戲配置創建 GameActivity
// 已移至 useGameActivityPush composable：
// - createGameActivityFromCurrentConfig
// - generateShortUrl
// - updateActivityWithShortUrl
// - createAndPushShortUrl

// 推送到終端（舊版本 HiTeach）
const sendToWebIRS = () => {
    messageBridge.postMessage({
        action: 'SendGame.WebIRS',
        data: {
            game: 'snakeGame',
            gameConfig: JSON.stringify({
                topic: gameConfig.topic,
                correctVocabularyList: [...gameConfig.vocabularyList],
                errorVocabularyList: [...gameConfig.errorVocabularyList],
                difficulty: gameConfig.difficulty,
                gameDuration: gameConfig.gameDuration,
                boardSize: gameConfig.boardSize,
                snakeSpeed: gameConfig.snakeSpeed,
                foodCount: gameConfig.foodCount
            })
        }
    });
};

// 實際執行推送邏輯
const executePush = async () => {
    console.log('按鈕點擊，當前禁用狀態：', isSendDisabled.value);
    console.log('HiTeach 版本：', htVersion.value, '支援新 Push 功能：', isNewPushFuncVersion.value);

    if (isSendDisabled.value) {
        $Message.warning({
            content: t('Snakegame.sendCooldown'),
            duration: 3,
            closable: true
        });
        return;
    }

    // 設置冷卻狀態
    isSendDisabled.value = true;
    console.log('設置為禁用狀態：', isSendDisabled.value);

    try {
        if (isNewPushFuncVersion.value) {
            // 新版本 HiTeach (>= 5.5.0.0002)：創建活動 + 推送短網址
            console.log('[HTSnakeGamePlay] 使用新版 Push 功能（創建活動 + 短網址）');

            // ✨ 修改：接收返回值
            const result = await createAndPushShortUrl(gameConfig, playerCount.value || 1); // Snake 遊戲 playerCount 使用 ref 值或預設 1

            // ✨ 新增：更新最新推送的活動 ID（覆蓋舊值）
            if (result.success && result.activityId) {
                pushedActivityId.value = result.activityId;
                pushedActivityName.value = gameConfig.topic || '貪食蛇遊戲';
                // ✅ 同步更新 classRosterSource（根據 useHiTeachRoster 判斷）
                classRosterSource.value = gameConfig.useHiTeachRoster ? 'HT' : 'general';
                console.log('[HTSnakeGamePlay] 已更新最新推送活動 ID:', result.activityId);
                console.log('[HTSnakeGamePlay] 已更新 classRosterSource:', classRosterSource.value);
                console.log('[HTSnakeGamePlay] ⚠️ 注意：這會覆蓋之前的 activityId');

                // ✨ 提示使用者可以查看成績
                $Message.info({
                    content: t('HTwbActKitHome.canViewScores') || '推送成功！您可以點擊查看成績按鈕查看學生提交的成績',
                    duration: 5,
                    closable: true
                });
            }
        } else {
            // 舊版本 HiTeach 或無版本資訊：推送完整 JSON（維持現有邏輯）
            console.log('[HTSnakeGamePlay] 使用舊版 Push 功能（推送 JSON）');
            sendToWebIRS();
            $Message.success({
                content: t('Snakegame.sendSuccess'),
                duration: 3,
                closable: true
            });
            // 注意：舊版本無法取得 activityId，查看成績按鈕不會顯示
        }
    } catch (error) {
        console.error('[HTSnakeGamePlay] Push 失敗:', error);
        // 錯誤訊息已在 createAndPushShortUrl 中顯示
    }

    // 1分鐘後恢復
    cooldownTimer.value = setTimeout(() => {
        isSendDisabled.value = false;
        console.log('恢復為啟用狀態：', isSendDisabled.value);
    }, 60000); // 60秒
};

// 處理發送協議按鈕點擊（WebView 環境）
const handleSendClick = () => {
    if (isSendDisabled.value) {
        $Message.warning({
            content: t('Snakegame.sendCooldown'),
            duration: 3,
            closable: true
        });
        return;
    }

    showPushConfirm.value = true;
};

// ✨ 開啟成績查看面板
const openScoresPanel = () => {
    if (!pushedActivityId.value) {
        $Message.warning({
            content: t('HTwbActKitHome.noActivityPushed') || '尚未推送活動，無法查看成績',
            duration: 3,
            closable: true
        });
        return;
    }
    showScoresPanel.value = true;
};

// ✨ 關閉成績查看面板
const closeScoresPanel = () => {
    showScoresPanel.value = false;
};

const pauseGame = () => {
    gamePaused.value = true;
    stopTimer();
    stopGameLoop();
    stopWordRefresh();
    // 暫停背景音樂
    if (backgroundMusic.value) {
        // eslint-disable-next-line no-empty
        try { backgroundMusic.value.pause(); } catch (e) {}
    }
};

const resumeGame = () => {
    gamePaused.value = false;
    startTimer();
    startGameLoop();
    startWordRefresh();
    // 繼續背景音樂
    if (backgroundMusic.value) {
        // eslint-disable-next-line no-empty
        try { backgroundMusic.value.play(); } catch (e) {}
    }
};

// 初始化雙人模式蛇的位置
const initTwoPlayerSnakes = () => {
    const boardSize = gameConfig.boardSize;

    // 左側玩家（綠色蛇）- 從左邊開始，向右移動
    leftSnake.value = [
        { x: 5, y: Math.floor(boardSize / 2) },
        { x: 4, y: Math.floor(boardSize / 2) },
        { x: 3, y: Math.floor(boardSize / 2) }
    ];
    leftDirection.value = 'right';
    leftNextDirection.value = 'right';
    leftScore.value = 0;
    leftCorrectCount.value = 0;
    leftErrorCount.value = 0;
    leftMaxSnakeLength.value = 3;

    // 右側玩家（橘色蛇）- 從右邊開始，向左移動
    rightSnake.value = [
        { x: boardSize - 6, y: Math.floor(boardSize / 2) },
        { x: boardSize - 5, y: Math.floor(boardSize / 2) },
        { x: boardSize - 4, y: Math.floor(boardSize / 2) }
    ];
    rightDirection.value = 'left';
    rightNextDirection.value = 'left';
    rightScore.value = 0;
    rightCorrectCount.value = 0;
    rightErrorCount.value = 0;
    rightMaxSnakeLength.value = 3;
};

const resetGame = () => {
    gameStarted.value = false;
    gamePaused.value = false;
    gameOver.value = false;
    beforeGame.value = true;
    showAnswerModal.value = false;
    scoreSubmitted.value = false; // 重置成績提交旗標，允許下次遊戲提交

    score.value = 0;
    correctCount.value = 0;
    errorCount.value = 0;
    maxSnakeLength.value = 3;
    timeRemaining.value = gameConfig.gameDuration;

    // 單人模式重置
    if (isSinglePlayerMode.value) {
        snake.value = [
            { x: 10, y: 10 },
            { x: 9, y: 10 },
            { x: 8, y: 10 }
        ];
        direction.value = 'right';
        nextDirection.value = 'right';
    } else {
        // 雙人模式重置
        initTwoPlayerSnakes();
    }

    foods.value = [];
    flyingScoreEffects.value = [];

    stopTimer();
    stopGameLoop();
};

// 根據選擇的遊戲模板進行跳轉
const navigateToSelectedGame = () => {
    // 如果選擇的是當前遊戲，不進行跳轉
    if (selectedGameTemplate.value === 'snake') {
        return;
    }

    // 取得 URL 參數
    const currentUrl = new URL(window.location.href);
    const actId = currentUrl.searchParams.get('actId');

    // 在 sessionStorage 標記已切換遊戲（跨頁面保存，並與 actId 綁定）
    if (actId) {
        sessionStorage.setItem('hasGameSwitched', JSON.stringify({
            actId,
            switched: true
        }));
    } else {
        // 非 actId 模式，清除先前的切換標記，改由 URL 的 switched=1 判斷
        sessionStorage.removeItem('hasGameSwitched');
    }

    // 保存當前遊戲資訊到 sessionStorage
    sessionStorage.setItem('switchedFromGame', 'snake');

    // 根據選擇的遊戲模板決定跳轉路徑
    let targetRoute = '';

    switch (selectedGameTemplate.value) {
        case 'mole':
            targetRoute = '/ht-molegame-play';
            break;
        case 'star':
            targetRoute = '/ht-pvpgame-play';
            break;
        default:
            console.error('未知的遊戲模板:', selectedGameTemplate.value);
            return;
    }

    // 構建完整的跳轉路徑
    if (actId) {
        // actId 模式：保留 actId 參數，並設定 switched=1
        router.push(`${targetRoute}?actId=${actId}&switched=1`);
    } else {
        // 其他模式：直接跳轉並設定 switched=1
        router.push(`${targetRoute}?switched=1`);
    }
};

// 提交遊戲成績到後端
const submitGameScore = async () => {
    // 防抖檢查：如果已經提交過，直接返回
    if (scoreSubmitted.value) {
        console.log('⚠️ 成績已經提交過，跳過重複提交');
        return;
    }

    // 只在正式活動模式且沒有切換遊戲時提交成績
    if (!isFromActId.value) {
        console.log('⚠️ 非正式活動模式，不提交成績');
        return;
    }

    if (hasGameSwitched.value) {
        console.log('⚠️ 因切換遊戲模板，不提交成績');
        return;
    }

    // 設置旗標，防止重複提交
    scoreSubmitted.value = true;

    try {
        // 從 sessionStorage 取得學生資訊
        const studentData = JSON.parse(sessionStorage.getItem('studentInfo') || '{}');

        // 從 URL 取得 actId
        const urlParams = new URLSearchParams(window.location.search);
        const actId = urlParams.get('actId');

        if (!actId) {
            console.error('無法提交成績：缺少 actId');
            scoreSubmitted.value = false;
            return;
        }

        if (!studentData.name) {
            console.error('無法提交成績：缺少學生姓名');
            scoreSubmitted.value = false;
            return;
        }

        // 單人模式
        if (isSinglePlayerMode.value) {
            const scoreData = {
                activityId: actId,
                studentName: studentData.name,
                seatNumber: studentData.seatNumber || '',
                score: score.value,
                correctCount: correctCount.value,
                wrongCount: errorCount.value
            };

            console.log('📤 提交成績資料（單人）:', scoreData);
            const response = await gameActivityAPI.submitScore(scoreData);

            if (response.status === 200) {
                console.log('✅ 成績提交成功:', response.data);
            }
        } else {
            // 雙人模式：分別提交兩位玩家的成績
            const scoreData1 = {
                activityId: actId,
                studentName: studentData.name,
                seatNumber: studentData.seatNumber || '',
                score: leftScore.value,
                correctCount: leftCorrectCount.value,
                wrongCount: leftErrorCount.value
            };

            const scoreData2 = {
                activityId: actId,
                studentName: studentData.name2,
                seatNumber: studentData.seatNumber2 || '',
                score: rightScore.value,
                correctCount: rightCorrectCount.value,
                wrongCount: rightErrorCount.value
            };

            console.log('📤 提交成績資料（玩家1）:', scoreData1);
            console.log('📤 提交成績資料（玩家2）:', scoreData2);

            // 並行提交兩位玩家的成績
            const [response1, response2] = await Promise.all([
                gameActivityAPI.submitScore(scoreData1),
                gameActivityAPI.submitScore(scoreData2)
            ]);

            if (response1.status === 200 && response2.status === 200) {
                console.log('✅ 兩位玩家成績提交成功');
            }
        }
    } catch (error) {
        console.error('❌ 提交成績失敗:', error);
        scoreSubmitted.value = false; // 失敗時重置旗標，允許重試
    }
};

const endGame = async () => {
    // 防抖檢查：如果遊戲已經結束，直接返回
    if (gameOver.value) {
        console.log('⚠️ 遊戲已經結束，避免重複調用 endGame');
        return;
    }

    gameOver.value = true;
    gameStarted.value = false;
    stopTimer();
    stopGameLoop();
    stopWordRefresh();
    // 清空雙人模式的按鍵追蹤
    pressedKeys.clear();
    // 停止背景音樂
    if (backgroundMusic.value) {
        // eslint-disable-next-line no-empty
        try { backgroundMusic.value.pause(); backgroundMusic.value.currentTime = 0; } catch (e) {}
    }
    // 播放遊戲結束音效
    playSound(gameOverSound.value);

    // 🆕 提交成績（僅限正式活動模式且未切換遊戲，且只提交一次）
    if (isFromActId.value && !hasGameSwitched.value && !scoreSubmitted.value) {
        await submitGameScore();
    } else if (isFromActId.value && hasGameSwitched.value) {
        // 切換模板後不提交成績，顯示提示訊息（與打地鼠一致）
        $Message.warning({
            content: t('gameActManager.templateSwitchedNoScore'),
            duration: 5,
            closable: true
        });
        console.log('⚠️ 因切換遊戲模板，不提交成績');
    }
};

// ==================== 計時器 ====================
const startTimer = () => {
    gameTimer = setInterval(() => {
        if (timeRemaining.value > 0) {
            timeRemaining.value--;
        } else {
            endGame();
        }
    }, 1000);
};

const stopTimer = () => {
    if (gameTimer) {
        clearInterval(gameTimer);
        gameTimer = null;
    }
};

// ==================== 遊戲循環 ====================
const startGameLoop = () => {
    gameLoopInterval = setInterval(() => {
        if (isSinglePlayerMode.value) {
            // 單人模式：只移動一條蛇
            moveSnake();
        } else {
            // 雙人模式：同步移動兩條蛇
            moveSnakePlayer('left');
            moveSnakePlayer('right');
        }
        checkCollisions();
    }, gameConfig.snakeSpeed);
};

const stopGameLoop = () => {
    if (gameLoopInterval) {
        clearInterval(gameLoopInterval);
        gameLoopInterval = null;
    }
};

// ==================== 單字批次刷新 ====================
const startWordRefresh = () => {
    stopWordRefresh();
    const intervalMs = Math.max(1, Number(gameConfig.wordBatchRefreshSec || 0)) * 1000;
    if (!intervalMs) return;
    wordRefreshInterval = setInterval(() => {
        if (gameStarted.value && !gamePaused.value && !gameOver.value) {
            initializeFoods();
        }
    }, intervalMs);
};

const stopWordRefresh = () => {
    if (wordRefreshInterval) {
        clearInterval(wordRefreshInterval);
        wordRefreshInterval = null;
    }
};

// ==================== 音效工具與初始化 ====================
const initSounds = () => {
    // 初始化音效來源（與打地鼠共用素材）
    readySound.value = new Audio(require('@/assets/HTwbActAssets/gameSounds/freeTTS-ready.mp3'));
    goSound.value = new Audio(require('@/assets/HTwbActAssets/gameSounds/freeTTS-go.mp3'));
    gameOverSound.value = new Audio(require('@/assets/HTwbActAssets/gameSounds/orchestral-win-331233.mp3'));
    // 摘星星用的收集音效（短促成功音）
    collectSound.value = new Audio(require('@/assets/HTwbActAssets/gameSounds/short-success-sound-glockenspiel-treasure-video-game-6346.mp3'));
    // 摘星星錯誤音效
    wrongSound.value = new Audio(require('@/assets/HTwbActAssets/gameSounds/wrong-answer-129254.mp3'));
    // 摘星星背景音樂（所有模式統一）
    backgroundMusic.value = new Audio(require('@/assets/HTwbActAssets/gameSounds/cute-happy-kids-354678.mp3'));
    backgroundMusic.value.loop = true;
    backgroundMusic.value.volume = 0.6;

    // 預加載
    readySound.value.load();
    goSound.value.load();
    gameOverSound.value.load();
    collectSound.value.load();
    wrongSound.value.load();
    backgroundMusic.value.load();
};

const playSound = (sound) => {
    if (isMuted.value || !sound) return;
    try {
        // 確保從頭播放
        sound.pause();
        sound.currentTime = 0;
        sound.play();
    } catch (e) {
        console.error('播放音效失敗:', e);
    }
};

const stopSound = (sound) => {
    if (!sound) return;
    try {
        sound.pause();
        sound.currentTime = 0;
    } catch (e) {
        // ignore
    }
};

onMounted(async () => {
    initSounds();
    await nextTick();
    // 初始量測
    updateBoardPixels();
    // 監聽棋盤元素大小變化（含媒體查詢、容器改變等）
    if (window.ResizeObserver) {
        boardResizeObserver = new ResizeObserver(() => {
            updateBoardPixels();
        });
        if (gameBoardRef.value) boardResizeObserver.observe(gameBoardRef.value);
    }
    // 後備：視窗尺寸變化時重新量測
    window.addEventListener('resize', updateBoardPixels);
});

onUnmounted(() => {
    stopSound(readySound.value);
    stopSound(goSound.value);
    stopSound(gameOverSound.value);
    stopSound(collectSound.value);
    stopSound(wrongSound.value);
    stopSound(backgroundMusic.value);
    if (boardResizeObserver) {
        if (typeof boardResizeObserver.disconnect === 'function') {
            boardResizeObserver.disconnect();
        }
        boardResizeObserver = null;
    }
    window.removeEventListener('resize', updateBoardPixels);
});

// ==================== 蛇的移動邏輯 ====================
// 單人模式的移動邏輯（保持向後兼容）
const moveSnake = () => {
    direction.value = nextDirection.value;
    const head = { ...snake.value[0] };

    switch (direction.value) {
        case 'up': head.y--; break;
        case 'down': head.y++; break;
        case 'left': head.x--; break;
        case 'right': head.x++; break;
    }

    snake.value.unshift(head);

    const foodIndex = checkFoodCollision();
    if (foodIndex === -1) {
        snake.value.pop();
    } else {
        const food = foods.value[foodIndex];
        handleFoodEaten(food, foodIndex);
    }

    if (snake.value.length > maxSnakeLength.value) {
        maxSnakeLength.value = snake.value.length;
    }
};

// 雙人模式的移動邏輯
const moveSnakePlayer = (player) => {
    if (player === 'left') {
        // 左側玩家（綠色蛇）
        leftDirection.value = leftNextDirection.value;
        const head = { ...leftSnake.value[0] };

        switch (leftDirection.value) {
            case 'up': head.y--; break;
            case 'down': head.y++; break;
            case 'left': head.x--; break;
            case 'right': head.x++; break;
        }

        leftSnake.value.unshift(head);

        const foodIndex = checkFoodCollisionPlayer('left');
        if (foodIndex === -1) {
            leftSnake.value.pop();
        } else {
            const food = foods.value[foodIndex];
            handleFoodEatenPlayer(food, foodIndex, 'left');
        }

        if (leftSnake.value.length > leftMaxSnakeLength.value) {
            leftMaxSnakeLength.value = leftSnake.value.length;
        }
    } else if (player === 'right') {
        // 右側玩家（橘色蛇）
        rightDirection.value = rightNextDirection.value;
        const head = { ...rightSnake.value[0] };

        switch (rightDirection.value) {
            case 'up': head.y--; break;
            case 'down': head.y++; break;
            case 'left': head.x--; break;
            case 'right': head.x++; break;
        }

        rightSnake.value.unshift(head);

        const foodIndex = checkFoodCollisionPlayer('right');
        if (foodIndex === -1) {
            rightSnake.value.pop();
        } else {
            const food = foods.value[foodIndex];
            handleFoodEatenPlayer(food, foodIndex, 'right');
        }

        if (rightSnake.value.length > rightMaxSnakeLength.value) {
            rightMaxSnakeLength.value = rightSnake.value.length;
        }
    }
};

const changeDirection = (newDirection, player = null) => {
    const opposites = {
        up: 'down',
        down: 'up',
        left: 'right',
        right: 'left'
    };

    if (isSinglePlayerMode.value || player === null) {
        // 單人模式
        if (opposites[newDirection] !== direction.value) {
            nextDirection.value = newDirection;
        }
    } else {
        // 雙人模式
        if (player === 'left') {
            if (opposites[newDirection] !== leftDirection.value) {
                console.log('🟢 [左側玩家] 方向變更:', leftDirection.value, '→', newDirection);
                leftNextDirection.value = newDirection;
            } else {
                console.log('🟢 [左側玩家] 方向被阻擋（反方向）:', newDirection);
            }
        } else if (player === 'right') {
            if (opposites[newDirection] !== rightDirection.value) {
                console.log('🟠 [右側玩家] 方向變更:', rightDirection.value, '→', newDirection);
                rightNextDirection.value = newDirection;
            } else {
                console.log('🟠 [右側玩家] 方向被阻擋（反方向）:', newDirection);
            }
        }
    }
};

// ==================== 碰撞檢測 ====================
const checkCollisions = () => {
    if (isSinglePlayerMode.value) {
        // 單人模式碰撞檢測
        const head = snake.value[0];

        // 撞牆檢測
        if (head.x < 0 || head.x >= gameConfig.boardSize ||
            head.y < 0 || head.y >= gameConfig.boardSize) {
            endGame();
            return;
        }

        // 撞自己檢測
        for (let i = 1; i < snake.value.length; i++) {
            if (head.x === snake.value[i].x && head.y === snake.value[i].y) {
                endGame();
                return;
            }
        }
    } else {
        // 雙人模式碰撞檢測
        const leftHead = leftSnake.value[0];
        const rightHead = rightSnake.value[0];

        // 1. 撞牆檢測（任一玩家撞牆都結束遊戲）
        if (leftHead.x < 0 || leftHead.x >= gameConfig.boardSize ||
            leftHead.y < 0 || leftHead.y >= gameConfig.boardSize ||
            rightHead.x < 0 || rightHead.x >= gameConfig.boardSize ||
            rightHead.y < 0 || rightHead.y >= gameConfig.boardSize) {
            endGame();
            return;
        }

        // 2. 撞自己檢測（各自獨立）
        // 左蛇撞自己
        for (let i = 1; i < leftSnake.value.length; i++) {
            if (leftHead.x === leftSnake.value[i].x &&
                leftHead.y === leftSnake.value[i].y) {
                endGame();
                return;
            }
        }

        // 右蛇撞自己
        for (let i = 1; i < rightSnake.value.length; i++) {
            if (rightHead.x === rightSnake.value[i].x &&
                rightHead.y === rightSnake.value[i].y) {
                endGame();
                return;
            }
        }

        // 3. 蛇與蛇碰撞：完全不檢測（關鍵需求）
        // 允許兩條蛇重疊、交叉、頭部相撞等任何情況，無任何影響
    }
};

const checkFoodCollision = () => {
    const head = snake.value[0];
    // 精確匹配：蛇頭必須完全對齊星星位置
    return foods.value.findIndex(f => f.x === head.x && f.y === head.y);
};

// 雙人模式的食物碰撞檢測
const checkFoodCollisionPlayer = (player) => {
    const head = player === 'left' ? leftSnake.value[0] : rightSnake.value[0];
    return foods.value.findIndex(f => f.x === head.x && f.y === head.y);
};

// ==================== 食物系統 ====================
const initializeFoods = () => {
    foods.value = [];
    for (let i = 0; i < gameConfig.foodCount; i++) {
        generateFood();
    }
};

const generateFood = () => {
    const isCorrect = Math.random() < 0.5;
    const wordList = isCorrect ? gameConfig.vocabularyList : gameConfig.errorVocabularyList;
    const word = wordList[Math.floor(Math.random() * wordList.length)];

    let x, y, attempts = 0;
    do {
        x = Math.floor(Math.random() * gameConfig.boardSize);
        y = Math.floor(Math.random() * gameConfig.boardSize);
        attempts++;
    } while ((isOccupied(x, y) || !isFoodAreaFree(x, y, word)) && attempts < 200);

    if (attempts < 200) {
        // 移除隨機偏移，確保星星與蛇頭使用相同的 grid 座標系統
        // 這樣視覺位置和碰撞檢測邏輯完全一致
        foods.value.push({
            x,
            y,
            word,
            isCorrect
        });
    }
};

const isOccupied = (x, y) => {
    if (isSinglePlayerMode.value) {
        // 單人模式：檢查單條蛇
        if (snake.value.some(s => s.x === x && s.y === y)) return true;
    } else {
        // 雙人模式：檢查兩條蛇
        if (leftSnake.value.some(s => s.x === x && s.y === y)) return true;
        if (rightSnake.value.some(s => s.x === x && s.y === y)) return true;
    }
    if (foods.value.some(f => f.x === x && f.y === y)) return true;
    return false;
};

function getCellSizeX() { return cellSizeX.value; }
function estimateWordWidthPx(word) { return word ? word.length * 12 : 0; }
function estimateWordCellsX(word) { return Math.max(1, Math.ceil(estimateWordWidthPx(word) / getCellSizeX())); }
function getFoodBBoxFor(x, y, word) {
    const half = Math.ceil(estimateWordCellsX(word) / 2);
    return {
        left: x - half,
        right: x + half,
        top: y - 1,
        bottom: y + 1
    };
}
function rectsOverlap(a, b) {
    return !(a.right < b.left || a.left > b.right || a.bottom < b.top || a.top > b.bottom);
}
function isFoodAreaFree(x, y, word) {
    const bbox = getFoodBBoxFor(x, y, word);
    if (bbox.left < 0 || bbox.top < 0 || bbox.right >= gameConfig.boardSize || bbox.bottom >= gameConfig.boardSize) return false;
    for (const f of foods.value) {
        const b2 = getFoodBBoxFor(f.x, f.y, f.word);
        if (rectsOverlap(bbox, b2)) return false;
    }
    return true;
}

const handleFoodEaten = (food, foodIndex) => {
    if (food.isCorrect) {
        score.value += 10;
        correctCount.value++;
        showFlyingScore('+10', food.x, food.y, true);
        // 正確：播放摘星星音效
        playSound(collectSound.value);
        // 答對：保留蛇的長度增加（在 moveSnake 中已經 unshift 了新頭部）
        // 不需要 pop()，所以蛇會變長
    } else {
        score.value = Math.max(0, score.value - 5);
        errorCount.value++;
        showFlyingScore('-5', food.x, food.y, false);
        // 錯誤：播放錯誤音效
        playSound(wrongSound.value);
        // 答錯：需要移除尾巴，保持蛇的長度不變
        // moveSnake 已經 unshift 新頭部，所以這裡 pop 尾巴即可
        snake.value.pop();
    }

    foods.value.splice(foodIndex, 1);
    generateFood();
};

// 雙人模式的食物處理
const handleFoodEatenPlayer = (food, foodIndex, player) => {
    if (player === 'left') {
        if (food.isCorrect) {
            leftScore.value += 10;
            leftCorrectCount.value++;
            showFlyingScore('+10', food.x, food.y, true);
            // 左側玩家正確：播放摘星星音效
            playSound(collectSound.value);
        } else {
            leftScore.value = Math.max(0, leftScore.value - 5);
            leftErrorCount.value++;
            showFlyingScore('-5', food.x, food.y, false);
            // 左側玩家錯誤：播放錯誤音效
            playSound(wrongSound.value);
            leftSnake.value.pop();
        }
    } else if (player === 'right') {
        if (food.isCorrect) {
            rightScore.value += 10;
            rightCorrectCount.value++;
            showFlyingScore('+10', food.x, food.y, true);
            // 右側玩家正確：播放摘星星音效
            playSound(collectSound.value);
        } else {
            rightScore.value = Math.max(0, rightScore.value - 5);
            rightErrorCount.value++;
            showFlyingScore('-5', food.x, food.y, false);
            // 右側玩家錯誤：播放錯誤音效
            playSound(wrongSound.value);
            rightSnake.value.pop();
        }
    }

    foods.value.splice(foodIndex, 1);
    generateFood();
};

const showFlyingScore = (text, gridX, gridY, isCorrect) => {
    // 使用 grid 座標直接計算像素位置，與食物星星的位置一致
    const cX = cellSizeX.value;
    const cY = cellSizeY.value;

    // 計算食物中心位置（與 getFoodStyle 中的計算一致）
    const foodLeft = gridX * cX;
    const foodTop = gridY * cY;
    const foodCenterX = foodLeft + cX / 2;
    const foodCenterY = foodTop + cY / 2;

    // 在食物中心位置稍微往上偏移
    const x = foodCenterX;
    const y = foodCenterY - cY * 0.3;

    const effect = {
        id: effectIdCounter++,
        text,
        x,
        y,
        isCorrect,
        opacity: 1
    };

    flyingScoreEffects.value.push(effect);

    const duration = 1000;
    const startTime = Date.now();
    const animate = () => {
        const elapsed = Date.now() - startTime;
        const progress = elapsed / duration;

        if (progress < 1) {
            effect.y = y - progress * 80;
            effect.opacity = 1 - progress;
            requestAnimationFrame(animate);
        } else {
            const index = flyingScoreEffects.value.findIndex(e => e.id === effect.id);
            if (index !== -1) {
                flyingScoreEffects.value.splice(index, 1);
            }
        }
    };
    requestAnimationFrame(animate);
};

// ==================== 答案彈窗 ====================
const toggleAnswerModal = () => {
    showAnswerModal.value = !showAnswerModal.value;
};

// ==================== 鍵盤控制 ====================
// 追蹤已按下的按鍵，避免重複觸發（僅雙人模式使用）
const pressedKeys = new Set();

const handleKeyPress = (event) => {
    // 只在遊戲進行中才響應鍵盤
    if (!gameStarted.value || gamePaused.value || gameOver.value) {
        return;
    }

    if (isSinglePlayerMode.value) {
        // 單人模式：只使用方向鍵，不需要處理同時按鍵
        const keyMap = {
            'ArrowUp': 'up',
            'ArrowDown': 'down',
            'ArrowLeft': 'left',
            'ArrowRight': 'right'
        };

        const newDirection = keyMap[event.key];
        if (newDirection) {
            event.preventDefault(); // 防止方向鍵滾動頁面
            changeDirection(newDirection);
        }
    } else {
        // 雙人模式：WASD + 方向鍵，需要支援同時按鍵

        // 防止按鍵重複觸發（長按時）
        if (pressedKeys.has(event.key)) {
            return;
        }
        pressedKeys.add(event.key);

        console.log('🎮 [鍵盤事件] 雙人模式按鍵:', event.key);

        const leftKeyMap = {
            'w': 'up', 'W': 'up',
            's': 'down', 'S': 'down',
            'a': 'left', 'A': 'left',
            'd': 'right', 'D': 'right'
        };

        const rightKeyMap = {
            'ArrowUp': 'up',
            'ArrowDown': 'down',
            'ArrowLeft': 'left',
            'ArrowRight': 'right'
        };

        let handled = false;

        // 左側玩家 (WASD)
        if (leftKeyMap[event.key]) {
            console.log('🟢 [左側玩家] WASD 按鍵:', event.key, '→ 方向:', leftKeyMap[event.key]);
            changeDirection(leftKeyMap[event.key], 'left');
            handled = true;
        }

        // 右側玩家 (方向鍵)
        if (rightKeyMap[event.key]) {
            console.log('🟠 [右側玩家] 方向鍵:', event.key, '→ 方向:', rightKeyMap[event.key]);
            changeDirection(rightKeyMap[event.key], 'right');
            handled = true;
        }

        // 只有當按鍵被處理時才阻止預設行為
        if (handled) {
            event.preventDefault();
        }
    }
};

// 監聽按鍵釋放，移除追蹤（僅雙人模式使用）
const handleKeyUp = (event) => {
    // 只在雙人模式時清理按鍵追蹤
    if (!isSinglePlayerMode.value) {
        pressedKeys.delete(event.key);
    }
};

// ==================== 預覽模式相關 ====================
// 載入預覽模式參數
const loadPreviewMode = (params) => {
    console.log('🐍 [預覽模式] 收到的完整參數:', params);
    isPreviewMode.value = true;
    console.log('🏷️ [預覽模式] isPreviewMode 已設定為:', isPreviewMode.value);

    // 讀取玩家人數（關鍵：支援雙人模式）
    playerCount.value = params.playerCount || 1;
    console.log('👥 [預覽模式] playerCount 已設定為:', playerCount.value);

    // 從 params.gameParameters 提取遊戲參數
    const gp = params.gameParameters || {};
    console.log('📋 [預覽模式] gameParameters:', gp);
    console.log('📝 [預覽模式] vocabularyList 數量:', gp.vocabularyList?.length || 0);
    console.log('❌ [預覽模式] errorVocabularyList 數量:', gp.errorVocabularyList?.length || 0);

    // 映射到 gameConfig 格式
    Object.assign(gameConfig, {
        topic: gp.topic || 'Preview',
        vocabularyList: gp.vocabularyList || gameConfig.vocabularyList,
        errorVocabularyList: gp.errorVocabularyList || gameConfig.errorVocabularyList,
        difficulty: gp.difficulty || 'medium',
        gameDuration: gp.gameDuration || 60,
        boardSize: gp.boardSize || 20,
        snakeSpeed: gp.snakeSpeed || 200,
        foodCount: gp.foodCount || 8,
        wordBatchRefreshSec: 8 // 固定值
    });

    // 重置遊戲時間
    timeRemaining.value = gameConfig.gameDuration;

    // 初始化雙人模式蛇的位置
    if (!isSinglePlayerMode.value) {
        initTwoPlayerSnakes();
    }

    console.log('✅ [預覽模式] gameConfig 已更新:', {
        topic: gameConfig.topic,
        vocabularyCount: gameConfig.vocabularyList.length,
        errorCount: gameConfig.errorVocabularyList.length,
        difficulty: gameConfig.difficulty,
        gameDuration: gameConfig.gameDuration
    });

    // 顯示預覽模式提示訊息（延遲顯示,確保頁面已完全載入）
    setTimeout(() => {
        try {
            if ($Message && typeof $Message.info === 'function') {
                $Message.info({
                    content: t('gameActManager.previewRefreshWarning'),
                    duration: 5,
                    closable: true
                });
            } else {
                console.warn('[貪食蛇預覽模式] $Message 未正確注入，使用 console 提示');
                console.info(`📢 ${t('gameActManager.previewRefreshWarning')}`);
            }
        } catch (error) {
            console.error('[貪食蛇預覽模式] 顯示提示訊息失敗:', error);
            console.info(`📢 ${t('gameActManager.previewRefreshWarning')}`);
        }
    }, 500);
};

// 預覽模式下的頁面重新整理警告
const handleBeforeUnload = (event) => {
    if (isPreviewMode.value) {
        // 現代瀏覽器要求設定 returnValue 為空字串才會顯示確認對話框
        event.preventDefault();
        event.returnValue = ''; // 必須設定為空字串
        return ''; // 相容舊版瀏覽器
    }
};

// 初始化 Auth Token
const initializeAuth = async () => {
    localStorage.removeItem("auth_token");
    localStorage.removeItem("auth_token_expiry");
    try {
        const res = await api.main.getTokenUnlogin();
        const exp = jwt_decode(res.auth_token).exp;
        localStorage.setItem("auth_token", res.auth_token);
        localStorage.setItem("auth_token_expiry", exp);
    } catch (error) {
        console.error("Error fetching auth token", error);
    }
};

// ==================== 生命週期 ====================
onMounted(async () => {
    console.log('🐍 貪食蛇遊戲掛載');

    // 載入 HiTeach 版本號
    loadVersion();

    // 0. 檢查是否從其他遊戲切換過來
    const urlParams = new URLSearchParams(window.location.search);
    const switched = urlParams.get('switched');

    if (switched === '1' || sessionStorage.getItem('hasGameSwitched') === 'true') {
        hasGameSwitched.value = true;
        console.log('🔄 檢測到遊戲切換，將不會提交成績');
    }

    // 1. 最優先檢查 actId 參數（正式活動模式）
    const actId = urlParams.get('actId');

    if (actId) {
        isFromActId.value = true;

        // 先初始化 auth_token（actId 模式需要匿名 token）
        await initializeAuth();

        try {
            // 呼叫公開 API 獲取活動資料
            const response = await gameActivityAPI.getGameActivity({ id: actId });
            console.log('🎮 API Response:', response);
            const activity = response.data;
            console.log('🎯 Activity Data:', activity);

            // 檢查活動是否開放進入（未開放則直接阻擋並提示）
            if (!activity.enableEntry) {
                $Message.warning({
                    content: t('gameActManager.activityNotOpen'),
                    duration: 0,
                    closable: true
                });
                beforeGame.value = false;
                return;
            }

            // 檢查 currentGame 是否與當前頁面匹配
            if (validateAndRedirectGame(activity, ['snake'], router, actId, switched)) {
                return; // 已重導向，中斷後續邏輯
            }

            // 讀取是否允許切換遊戲模板
            allowTemplateChange.value = activity.allowTemplateChange ?? true;
            console.log('🔀 Allow Template Change:', allowTemplateChange.value);

            // 解析 HiTeach 名單資料
            classRosterSource.value = activity.classRosterSource || '';
            useHiTeachRoster.value = activity.classRosterSource === 'HT';

            if (classRosterSource.value === 'HT' && activity.classRoster) {
                try {
                    classRoster.value = JSON.parse(activity.classRoster);
                    console.log('👥 HiTeach 名單:', classRoster.value);
                } catch (error) {
                    console.error('❌ 解析 classRoster 失敗:', error);
                    classRoster.value = [];
                }
            }

            // 檢查是否為原始遊戲（用於判斷是否提交成績）
            const originalGame = activity.currentGame;
            const isOriginalGame = originalGame === 'snake';
            console.log('🎯 Original Game:', originalGame, 'Is Original:', isOriginalGame);

            if (!isOriginalGame && !hasGameSwitched.value) {
                hasGameSwitched.value = true;
                console.log('🔄 此活動原始遊戲不是貪食蛇，標記為已切換');
            }

            // 解析 gameContentSettings (JSON 字串)
            const gameParameters = activity.gameContentSettings
                ? JSON.parse(activity.gameContentSettings)
                : {};

            // 讀取玩家人數
            playerCount.value = activity.playerCount || 1;
            console.log('👥 Player Count:', playerCount.value);

            // 初始化雙人模式蛇的位置
            if (!isSinglePlayerMode.value) {
                initTwoPlayerSnakes();
            }

            // 映射到 gameConfig 格式
            Object.assign(gameConfig, {
                topic: activity.activityName || '貪食蛇挑戰',
                vocabularyList: gameParameters.vocabularyList || gameConfig.vocabularyList,
                errorVocabularyList: gameParameters.errorVocabularyList || gameConfig.errorVocabularyList,
                difficulty: gameParameters.difficulty || 'medium',
                gameDuration: gameParameters.gameDuration || 60,
                boardSize: gameParameters.boardSize || 20,
                snakeSpeed: gameParameters.snakeSpeed || 200,
                foodCount: gameParameters.foodCount || 8,
                wordBatchRefreshSec: 8
            });

            // 重置遊戲時間
            timeRemaining.value = gameConfig.gameDuration;

            console.log('✅ [actId 模式] gameConfig 已更新:', {
                topic: gameConfig.topic,
                vocabularyCount: gameConfig.vocabularyList.length,
                errorCount: gameConfig.errorVocabularyList.length,
                difficulty: gameConfig.difficulty,
                gameDuration: gameConfig.gameDuration,
                playerCount: playerCount.value
            });

            // 顯示活動載入成功訊息
            $Message.success({
                content: t('gameActManager.activityLoadSuccess'),
                duration: 2,
                closable: true
            });

            // 添加鍵盤監聽
            window.addEventListener('keydown', handleKeyPress);
            window.addEventListener('keyup', handleKeyUp);

            return; // 完成 actId 模式初始化，跳出
        } catch (error) {
            console.error('獲取活動資料失敗:', error);
            $Message.error(t('gameActManager.activityLoadFailed'));
        }
    }

    // 2. 檢查預覽模式
    const previewData = localStorage.getItem('game_preview_data');
    const previewTimestamp = localStorage.getItem('game_preview_timestamp');

    console.log('[貪食蛇] localStorage 檢查:', {
        hasPreviewData: !!previewData,
        hasTimestamp: !!previewTimestamp,
        previewDataLength: previewData?.length || 0
    });

    if (previewData && previewTimestamp) {
        const now = Date.now();
        const timestamp = parseInt(previewTimestamp, 10);
        const fiveMinutes = 5 * 60 * 1000;
        const timeElapsed = now - timestamp;

        console.log('[貪食蛇] 時效檢查:', {
            timeElapsed: `${(timeElapsed / 1000).toFixed(1)}秒`,
            isValid: timeElapsed < fiveMinutes,
            threshold: `${fiveMinutes / 1000}秒`
        });

        // 檢查是否在 5 分鐘內
        if (now - timestamp < fiveMinutes) {
            try {
                const params = JSON.parse(previewData);

                console.log('[貪食蛇] 預覽參數解析成功:', {
                    currentGame: params.currentGame,
                    playerCount: params.playerCount,
                    hasGameParameters: !!params.gameParameters
                });

                // 僅當 currentGame 為 'snake' 時才載入預覽模式
                if (params.currentGame === 'snake') {
                    loadPreviewMode(params);

                    // 延遲清除 localStorage（30秒後），避免頁面重新整理時資料遺失
                    setTimeout(() => {
                        localStorage.removeItem('game_preview_data');
                        localStorage.removeItem('game_preview_timestamp');
                        console.log('[貪食蛇] 預覽資料已清除（30秒後）');
                    }, 30000); // 30秒後清除

                    // 添加頁面重新整理警告
                    window.addEventListener('beforeunload', handleBeforeUnload);

                    // 跳出，不執行後續邏輯
                    // 添加鍵盤監聽
                    window.addEventListener('keydown', handleKeyPress);
                    window.addEventListener('keyup', handleKeyUp);
                    return;
                } else {
                    console.log('[貪食蛇] currentGame 不是 snake，跳過預覽模式載入');
                }
            } catch (error) {
                console.error('[貪食蛇] 預覽參數解析失敗:', error);
                localStorage.removeItem('game_preview_data');
                localStorage.removeItem('game_preview_timestamp');
            }
        } else {
            console.log('[貪食蛇] 預覽資料已過期，清除 localStorage');
            localStorage.removeItem('game_preview_data');
            localStorage.removeItem('game_preview_timestamp');
        }
    }

    // 2. 如果沒有預覽模式，使用預設 Demo 模式
    console.log('[貪食蛇] 使用預設 Demo 模式');

    // 3. 發送初始化訊息，並接收遊戲配置（WebView/Demo 模式）
    messageBridge.postMessage({
        action: 'Init.SnakeGame',
        data: {
            topic: gameConfig.topic,
            correctVocabularyList: [...gameConfig.vocabularyList],
            errorVocabularyList: [...gameConfig.errorVocabularyList],
            difficulty: gameConfig.difficulty,
            gameDuration: gameConfig.gameDuration,
            boardSize: gameConfig.boardSize,
            snakeSpeed: gameConfig.snakeSpeed,
            foodCount: gameConfig.foodCount
        }
    });

    // 保存監聽器引用以便在 onUnmounted 時移除
    const snakeGameMessageHandler = (event) => {
        const msg = event.data;
        console.log(msg,"貪食蛇頁面");

        // 監聽 Init.SnakeGame 訊息，載入遊戲配置
        // 注意：Init.GameHome 已由 useGameMessageBridge composable 統一處理
        if (msg.action === 'Init.SnakeGame') {
            Object.assign(gameConfig, msg.data);
            timeRemaining.value = gameConfig.gameDuration;
            // 將設定存入 sessionStorage
            sessionStorage.setItem('SnakeGameConfig', JSON.stringify(msg.data));
        }
    };
    messageBridge.addEventListener(snakeGameMessageHandler);

    // 儲存監聽器引用到外部變數
    messageHandlerRef = snakeGameMessageHandler;

    // 添加鍵盤監聽
    window.addEventListener('keydown', handleKeyPress);
    window.addEventListener('keyup', handleKeyUp);
});

onUnmounted(() => {
    stopTimer();
    stopGameLoop();
    // 清理冷卻計時器
    clearTimeout(cooldownTimer.value);
    // 移除鍵盤監聽
    window.removeEventListener('keydown', handleKeyPress);
    window.removeEventListener('keyup', handleKeyUp);
    // 移除頁面重新整理警告
    window.removeEventListener('beforeunload', handleBeforeUnload);
    // 移除 messageBridge 監聽器
    if (messageHandlerRef) {
        messageBridge.removeEventListener(messageHandlerRef);
        messageHandlerRef = null;
    }
    // 清空雙人模式的按鍵追蹤
    pressedKeys.clear();
});
</script>

<style scoped lang="less">
@import '@/assets/HTwbActKitCSS/HTsnakeGame.less';
</style>
